import './index.css'
import cn from 'clsx'

import React from 'react'
import { useSelector } from 'hooks/use-redux'
import { isDeviceTypePhone } from '@disco/state-management/selectors/app'
import Button from 'components/ui/button'
import DiscoModal from 'components/ui/disco-modal'
import { Theme } from '@radix-ui/themes'
import { modalWindowStyles, ModalStyle } from 'components/utils/modalStyles'

type Props = {
  actions?: React.ReactNode
  children?: React.ReactNode
  footer?: React.ReactNode
  header?: React.ReactNode
  contextualHeader?: React.ReactNode
  isOpen: boolean
  width?: 'small' | 'medium' | 'large'
  noMinHeight?: boolean
  noPaddingBottom?: boolean
  onClose?: (...args: Array<any>) => any
  hiddenCloseButton?: boolean
  overflowVisible?: boolean
  modalStyle?: ModalStyle
}
const defaultModalStyle = {
  modalWindowStyles: {
    ...modalWindowStyles,
    content: {
      ...modalWindowStyles.content,
      backgroundColor: '#fff',
    },
  },
}
/**
 * @deprecated Use `Dialog` from `components/ds` instead. Gradual migration recommended.
 * @see {@link components/ds/Dialog}
 * @migration
 * - Replace with DS Dialog compound component:
 *   ```tsx
 *   // Legacy
 *   <ModalWindow
 *     isOpen={isOpen}
 *     header={<Heading>Title</Heading>}
 *     onClose={handleClose}
 *     actions={<Button>Save</Button>}
 *   >
 *     Content here
 *   </ModalWindow>
 *
 *   // DS alternative
 *   <Dialog.Root open={isOpen} onOpenChange={setIsOpen}>
 *     <Dialog.Content>
 *       <Dialog.Title>Title</Dialog.Title>
 *       <Box>Content here</Box>
 *       <Flex gap="3" mt="4" justify="end">
 *         <Button>Save</Button>
 *       </Flex>
 *     </Dialog.Content>
 *   </Dialog.Root>
 *   ```
 * - Prop mapping:
 *   - isOpen → `open` prop on Dialog.Root
 *   - onClose → `onOpenChange` on Dialog.Root
 *   - header → `<Dialog.Title>` or custom header content
 *   - children → Content inside Dialog.Content
 *   - footer/actions → Compose with `<Flex>` at bottom of Dialog.Content
 *   - width → Use `maxWidth` prop on Dialog.Content
 *   - hiddenCloseButton → Don't render close button (DS Dialog has built-in close)
 * - Custom layouts (contextualHeader, noMinHeight, etc.) require composing with DS layout components
 * - Uses react-modal internally; DS Dialog uses Radix Dialog primitives
 *
 * A customizable modal window component with header, content, and footer areas
 * @param actions - Action buttons to display in the modal footer
 * @param children - Main content to display in the modal body
 * @param footer - Additional footer content to display alongside actions
 * @param header - Header content to display at the top of the modal
 * @param contextualHeader - Additional contextual header content below the main header
 * @param isOpen - Whether the modal is currently open/visible
 * @param width - Size of the modal window (default: 'large')
 * @param noMinHeight - Whether to disable the minimum height constraint
 * @param noPaddingBottom - Whether to remove bottom padding from the content area
 * @param onClose - Callback function called when the modal should close
 * @param hiddenCloseButton - Whether to hide the close button in the header
 * @param overflowVisible - Whether content should be allowed to overflow the modal bounds
 * @param modalStyle - Custom styling for the modal window (default: defaultModalStyle.modalWindowStyles)
 * @returns Modal window component with configurable layout and styling
 */
const ModalWindow = ({
  actions,
  children,
  footer,
  header,
  contextualHeader,
  isOpen,
  width = 'large',
  noMinHeight,
  noPaddingBottom,
  onClose,
  hiddenCloseButton,
  overflowVisible,
  modalStyle = defaultModalStyle.modalWindowStyles,
}: Props): React.ReactNode => {
  const isPhone = useSelector(isDeviceTypePhone)
  return (
    <DiscoModal
      closeTimeoutMS={200}
      onRequestClose={onClose}
      isOpen={isOpen}
      style={modalStyle}
    >
      <Theme hasBackground>
        <div
          className={cn('ModalWindow', {
            'ModalWindow--small': width === 'small',
            'ModalWindow--medium': width === 'medium',
            'ModalWindow--large': width === 'large',
            'ModalWindow--overflow-visible': overflowVisible,
          })}
        >
          <div className="ModalWindow-header">
            <div className="ModalWindow-header-content">{header}</div>
            {!hiddenCloseButton && (
              <div className="ModalWindow-header-close">
                <Button
                  appearance="icon"
                  icon="close"
                  onClick={onClose}
                  compact={isPhone}
                />
              </div>
            )}
          </div>

          {contextualHeader && (
            <div className="ModalWindow-contextual-header">
              <div className="ModalWindow-header-content">
                {contextualHeader}
              </div>
            </div>
          )}

          {/* TO DO: noMinHeight, refrain from using, this will be revised in JIRA Ticket - [https://disco-team.atlassian.net/browse/DISCO-6113]  */}
          <div
            className={cn('ModalWindow-content', {
              'ModalWindow--noMinHeight': noMinHeight,
              'ModalWindow--noPaddingBottom': noPaddingBottom,
              'ModalWindow--overflow-visible': overflowVisible,
            })}
          >
            {children}
          </div>
          {(footer || actions) && (
            <div className="ModalWindow-footer">
              {footer && (
                <div className="ModalWindow-footer-content">{footer}</div>
              )}
              <div className="ModalWindow-actions">{actions}</div>
            </div>
          )}
        </div>
      </Theme>
    </DiscoModal>
  )
}

export default ModalWindow
